import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Check, X, ArrowRight, Trophy, Lock } from "lucide-react";
import {
  buildDailySession,
  QUESTIONS_PER_DAY,
  todayKey,
  msUntilTomorrow,
  formatCountdown,
} from "@/lib/quiz";
import {
  loadState,
  saveState,
  resetIfNewDay,
  archiveTodayIfComplete,
} from "@/lib/storage";

const FEEDBACK_MS = 1000;

export default function QuizPage() {
  const [state, setState] = useState(() => resetIfNewDay(loadState()));
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [countdown, setCountdown] = useState(msUntilTomorrow());
  const timerRef = useRef(null);

  // Initialize today's session if needed.
  useEffect(() => {
    const next = resetIfNewDay({ ...state });
    if (!next.today) {
      next.today = {
        date: todayKey(),
        session: buildDailySession(),
        index: 0,
        correct: 0,
      };
      saveState(next);
      setState(next);
    } else {
      saveState(next);
      setState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live countdown for locked state.
  useEffect(() => {
    const i = setInterval(() => setCountdown(msUntilTomorrow()), 1000);
    return () => clearInterval(i);
  }, []);

  const today = state.today;
  const isComplete = today && today.index >= QUESTIONS_PER_DAY;
  const current = useMemo(() => {
    if (!today || isComplete) return null;
    return today.session[today.index];
  }, [today, isComplete]);

  const handleSelect = (option) => {
    if (locked || !current) return;
    setLocked(true);
    setSelected(option);

    const isCorrect = option === current.capital;

    timerRef.current = setTimeout(() => {
      setState((prev) => {
        const updated = { ...prev };
        updated.today = {
          ...prev.today,
          index: prev.today.index + 1,
          correct: prev.today.correct + (isCorrect ? 1 : 0),
        };
        updated.totalAnswered = prev.totalAnswered + 1;
        updated.totalCorrect = prev.totalCorrect + (isCorrect ? 1 : 0);

        if (updated.today.index >= QUESTIONS_PER_DAY) {
          archiveTodayIfComplete(updated);
        }
        saveState(updated);
        return updated;
      });
      setSelected(null);
      setLocked(false);
    }, FEEDBACK_MS);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!today) {
    return (
      <div className="py-10 text-center text-[#52525B]" data-testid="loading-state">
        Loading…
      </div>
    );
  }

  // ─────────────────────────── Completed (Locked) State ─────────────────────────
  if (isComplete) {
    const pct =
      today.index === 0 ? 0 : Math.round((today.correct / today.index) * 100);
    return (
      <section className="flex flex-col items-center text-center pt-6" data-testid="locked-state">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-4">
          <Lock className="h-3.5 w-3.5" />
          Today is complete
        </div>
        <Trophy className="h-10 w-10 text-[#0A0A0A] mb-4" strokeWidth={1.75} />
        <h1
          className="text-5xl sm:text-6xl font-black tracking-tighter text-[#0A0A0A] leading-none"
          data-testid="score-reveal-text"
        >
          {today.correct}/{QUESTIONS_PER_DAY}
        </h1>
        <p className="text-base text-[#52525B] mt-3">
          {pct}% accuracy today
        </p>

        <div className="w-full mt-10 border-t border-[#E4E4E7] pt-8">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-2">
            Next quiz in
          </div>
          <div
            className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A] tabular-nums"
            data-testid="countdown-timer"
          >
            {formatCountdown(countdown)}
          </div>
        </div>

        <Link
          to="/stats"
          className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A] underline underline-offset-4 hover:no-underline"
          data-testid="view-stats-link"
        >
          View your stats
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    );
  }

  // ─────────────────────────── Active Quiz State ──────────────────────────────
  const progressValue = (today.index / QUESTIONS_PER_DAY) * 100;

  return (
    <section className="flex flex-col" data-testid="quiz-active">
      <div className="flex items-baseline justify-between mb-2">
        <span
          className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B]"
          data-testid="daily-progress-text"
        >
          Question {today.index + 1} of {QUESTIONS_PER_DAY}
        </span>
        <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B]">
          {today.correct} correct
        </span>
      </div>
      <Progress
        value={progressValue}
        className="h-1 rounded-none bg-[#E4E4E7] [&>*]:bg-[#0A0A0A] [&>*]:rounded-none"
        data-testid="progress-bar"
      />

      <div className="mt-10 sm:mt-14">
        <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-3">
          What is the capital of
        </div>
        <h1
          key={today.index}
          className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A] leading-[1.05] animate-[fadeUp_320ms_ease-out]"
          data-testid="question-text"
        >
          {current.country}?
        </h1>
      </div>

      <div className="mt-10 flex flex-col gap-3" key={`opts-${today.index}`}>
        {current.options.map((opt, i) => {
          const isSelected = selected === opt;
          const isCorrectAnswer = locked && opt === current.capital;
          const isWrongSelected = isSelected && opt !== current.capital;

          let stateCls =
            "border-[#E4E4E7] bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]";
          if (isCorrectAnswer)
            stateCls = "border-[#16A34A] bg-[#16A34A] text-white";
          else if (isWrongSelected)
            stateCls =
              "border-[#DC2626] bg-[#DC2626] text-white animate-[shake_320ms_ease-in-out]";
          else if (locked)
            stateCls = "border-[#E4E4E7] bg-white text-[#A1A1AA]";

          return (
            <button
              key={opt + i}
              onClick={() => handleSelect(opt)}
              disabled={locked}
              data-testid={`answer-button-${i}`}
              className={`group w-full text-left border rounded-md px-5 py-4 min-h-[60px] flex items-center justify-between transition-colors duration-150 ${stateCls} disabled:cursor-not-allowed`}
            >
              <span className="text-base font-medium">{opt}</span>
              {isCorrectAnswer && <Check className="h-5 w-5" />}
              {isWrongSelected && <X className="h-5 w-5" />}
            </button>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-[#A1A1AA]">
        {QUESTIONS_PER_DAY} questions per day · resets at midnight
      </p>
    </section>
  );
}
